import SearchIcon from '@jetbrains/icons/search.svg'
import fuzzyHighlight from '@jetbrains/ring-ui/components/global/fuzzy-highlight'
import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import type {SelectItem} from '@jetbrains/ring-ui/components/select/select'
import type React from 'react'
import {useCallback, useMemo, useState} from 'react'

import {useAppSelector} from '../../../../../../../../../hooks/react-redux'
import {emptyArray, emptyObject} from '../../../../../../../../../utils/empty'
import useOutsideRepository from '../../../../../../CreatePipelinePage/Repositories/RepositoryList/useOutsideRepository'
import {getHttpsRepositoryUrlFromSsh} from '../../../../../../CreatePipelinePage/Repositories/RepositoryList/useOutsideRepository.utils'
import {usePipelineId} from '../../../../../../hooks/pipeline'
import type {VcsRootDto} from '../../../../../../services/pipelinesApi.types'
import {getDraft} from '../../../../../EditPipelinePage.selectors'
import type {PipelineRepository, RepositorySelectItem} from '../../PipelineRepositories.types'

import {
  getRepositorieAutocompleteOptions,
  getRepositoryIcon,
} from './PipelineRepositoryAutocomplete.utils'

const duplicatedRepositoryUrlErrorMessage = 'This repository is already used'
const failedReadRepositoryErrorMessage =
  'Failed to read repository metadata. (Ensure the URL is correct and you have enough permissions to access this repo, and try again.)'

function usePipelineRepositoryAutocomplete(
  repositories: PipelineRepository[],
  onChange: (value: RepositorySelectItem | null) => void,
  selected?: SelectItem | null,
) {
  const pipelineId = usePipelineId()
  const [searchValue, setSearchValue] = useState<string>('')
  const mainRepository =
    useAppSelector(state => getDraft(state, pipelineId)?.vcsRoot) ?? (emptyObject as VcsRootDto)
  const additionalVcsRoots =
    useAppSelector(state => getDraft(state, pipelineId)?.additionalVcsRoots) ?? emptyArray
  const createdVcsRootsSet = useMemo(
    () => new Set([mainRepository, ...additionalVcsRoots].map(({url}) => url)),
    [additionalVcsRoots, mainRepository],
  )
  const {
    isRepositoryTestConnection,
    isUrlVerifiedRepository,
    getRepositoryDefaultBranch,
    isRepositoryTestConnectionLoading,
    isRepositoryBranchesFetching,
    isRepositoryTestConnectionError,
  } = useOutsideRepository(searchValue)
  const isDuplicatedRepositoryUrl = isUrlVerifiedRepository && createdVcsRootsSet.has(searchValue)
  const autocompleteOptions: RepositorySelectItem[] = useMemo(
    () =>
      getRepositorieAutocompleteOptions(
        searchValue,
        repositories,
        isRepositoryTestConnection &&
          !isRepositoryTestConnectionLoading &&
          isUrlVerifiedRepository &&
          !isDuplicatedRepositoryUrl,
      ),
    [
      isDuplicatedRepositoryUrl,
      isRepositoryTestConnection,
      isRepositoryTestConnectionLoading,
      isUrlVerifiedRepository,
      repositories,
      searchValue,
    ],
  )
  const onChangeSelect = useCallback(
    async (selectedItem: RepositorySelectItem | null) => {
      const {key, title = '', label, htmlUrl = '', defaultBranch = ''} = selectedItem ?? {}
      const isTypingEvent = typeof key === 'number'
      const selectNameValue = isTypingEvent ? label?.toString() ?? '' : title
      const name = getHttpsRepositoryUrlFromSsh(selectNameValue).replace('.git', '')
      const isSelectedItem = selectedItem != null && htmlUrl

      if (isSelectedItem) {
        const branch = !defaultBranch ? await getRepositoryDefaultBranch(htmlUrl) : defaultBranch
        onChange({...selectedItem, defaultBranch: branch})
      } else {
        onChange(selectedItem)
      }

      setSearchValue(name)
    },
    [getRepositoryDefaultBranch, onChange],
  )
  const autocompleteFilterHandler = (itemToCheck: RepositorySelectItem, checkString: string) => {
    const value = getHttpsRepositoryUrlFromSsh(checkString).replace('.git', '')
    const {matched} = fuzzyHighlight(value, itemToCheck?.htmlUrl ?? '')

    return matched
  }
  const repositoryIcon = useMemo(() => getRepositoryIcon(`${selected?.key}`), [selected])
  const filterRepositoryIcon = repositoryIcon != null ? repositoryIcon : SearchIcon
  const autocompleteFilterIcon =
    isRepositoryTestConnectionLoading || isRepositoryBranchesFetching
      ? LoaderInline
      : filterRepositoryIcon
  const selectSearchValueHandler = () => {
    const isTypingValue = typeof selected?.key === 'number'
    const selectedOption = autocompleteOptions?.find(
      ({login, name, key}) =>
        name === searchValue || `${login}/${name}` === searchValue || key === searchValue,
    )

    if (isTypingValue && selectedOption) {
      onChangeSelect(selectedOption)
    }
  }
  const autocompleteKeyDownHandler = ({key}: React.KeyboardEvent) => {
    if (key === 'Enter') {
      selectSearchValueHandler()
    }
  }
  const autocompleteBlurHandler = () => selectSearchValueHandler()
  const notFoundMessage = isDuplicatedRepositoryUrl ? duplicatedRepositoryUrlErrorMessage : ''
  const errorMessage =
    !isRepositoryTestConnectionLoading && isUrlVerifiedRepository && isRepositoryTestConnectionError
      ? failedReadRepositoryErrorMessage
      : null

  return {
    notFoundMessage,
    errorMessage,
    autocompleteFilterIcon,
    autocompleteOptions,
    onChangeSelect,
    autocompleteFilterHandler,
    autocompleteBlurHandler,
    autocompleteKeyDownHandler,
  }
}

export default usePipelineRepositoryAutocomplete
